import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import type { SelectIdea } from "@db/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { IdeaDialog } from "./idea-dialog";
import { colors } from "@/lib/colors";

// change colors here
const tagColors = {
  Reduce: "bg-[#5a8ba7]/10 text-[#5a8ba7]",
  Reform: "bg-[#4b4f54]/10 text-[#4b4f54]",
  Advance: "bg-[#dc582a]/10 text-[#dc582a]",
  Expand: "bg-[#799b3e]/10 text-[#799b3e]",
} as const;

const statusColors = {
  'Needs Review': 'bg-red-500 text-white',
  'Under Consideration': 'bg-[#5a8ba7] text-white',
  'In Progress': 'bg-[#799b3e] text-white',
  'Rejected': 'bg-[#4b4f54]/50 text-white',
  'Complete': 'bg-[#4b4f54] text-white',
} as const;

export function IdeaCard({ idea }: { idea: SelectIdea }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const voteMutation = useMutation({
    mutationFn: async (isUpvote: boolean) => {
      if (typeof isUpvote !== 'boolean') {
        throw new Error('Invalid vote type');
      }

      const res = await fetch(`/api/ideas/${idea.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isUpvote }),
        credentials: "include",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Failed to vote' }));
        throw new Error(errorData.message || 'Failed to vote');
      }

      const data = await res.json();

      // Validate the response data
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response from server');
      }

      // Extract the updated idea data from the server response
      const updatedIdea = {
        upvotes: data.idea?.upvotes ?? idea.upvotes,
        downvotes: data.idea?.downvotes ?? idea.downvotes,
        userVote: data.userVote ?? idea.userVote,
        action: data.action
      };

      return updatedIdea;
    },
    onSuccess: (updatedIdea) => {
      // Update all queries that contain this idea
      queryClient.setQueriesData({ queryKey: ["/api/ideas"] }, (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;

        return oldData.map(oldIdea => {
          if (oldIdea.id !== idea.id) return oldIdea;

          // Ensure userVote is explicitly null when vote is removed
          const newUserVote = updatedIdea.action === 'removed' ? null : updatedIdea.userVote;

          return {
            ...oldIdea,
            upvotes: updatedIdea.upvotes,
            downvotes: updatedIdea.downvotes,
            userVote: newUserVote
          };
        });
      });

      // Show success toast based on action
      const actionMessages = {
        added: 'Vote added successfully',
        removed: 'Vote removed successfully',
        changed: 'Vote changed successfully'
      };

      toast({
        title: "Success",
        description: actionMessages[updatedIdea.action as keyof typeof actionMessages] || 'Vote updated successfully',
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to vote",
      });
    }
  });

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card
          className="h-full hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => setDialogOpen(true)}
        >
          <CardContent className="pt-6 h-[160px] flex flex-col">
            <div className="mb-2">
              <h3 className="text-xl font-semibold">{idea.title}</h3>
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-gray-600 line-clamp-3 break-words">{idea.description}</p>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col border-t pt-4 pb-4 gap-3">
            <div className="flex justify-between w-full">
              <div className="flex gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-green-600 hover:text-green-700 hover:bg-green-50 ${idea.userVote === true ? "bg-green-50" : ""
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!voteMutation.isPending) {
                      voteMutation.mutate(true);
                    }
                  }}
                  disabled={voteMutation.isPending}
                >
                  <ThumbsUp
                    className={`h-4 w-4 mr-1 ${idea.userVote === true ? "fill-green-600" : "stroke-current"
                      }`}
                  />
                  {idea.upvotes}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-red-600 hover:text-red-700 hover:bg-red-50 ${idea.userVote === false ? "bg-red-50" : ""
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!voteMutation.isPending) {
                      voteMutation.mutate(false);
                    }
                  }}
                  disabled={voteMutation.isPending}
                >
                  <ThumbsDown
                    className={`h-4 w-4 mr-1 ${idea.userVote === false ? "fill-red-600" : "stroke-current"
                      }`}
                  />
                  {idea.downvotes}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  {idea.commentCount || 0}
                </Button>
              </div>

              <div className="flex flex-col items-end">
                <span className="text-sm text-gray-500">
                  {new Date(idea.createdAt).toLocaleDateString()}
                </span>
                <span className="text-sm text-purple-600 font-medium">
                  {idea.username || 'Anonymous'}
                </span>
              </div>
            </div>

            <div className="flex gap-2 justify-end w-full">
              <Badge
                variant="outline"
                className={`border-transparent whitespace-nowrap ${tagColors[idea.tag as keyof typeof tagColors]}`}
              >
                {idea.tag}
              </Badge>
              <Badge
                variant="outline"
                className={`border-transparent whitespace-nowrap inline-flex px-3 ${statusColors[idea.status as keyof typeof statusColors]}`}
              >
                {idea.status}
              </Badge>
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <IdeaDialog
        idea={idea}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}